// W36: thin wrapper over the generated billing client.
//
// Wraps the billing service's tenant surface:
//   /v1/billing/wallet · /v1/billing/ledger · /v1/billing/credits/grant
//   /v1/payments/topup · /v1/invoices (W41)
//   /v1/billing/usage/* · /v1/billing/alerts · /v1/payments/methods ·
//   /v1/payments/auto-topup/run (usage metering, 2026-09-05)
//   /v1/billing/extra-use (extra use on Growth and Scale, 2026-10-30)
//   /v1/admin/ledger (platform admin; the cursor-paged form: see listAdminLedger)
//
// Plan allowances are the one part of this service with no method here.
// `GET /internal/plan-allowance` is inside the service network and the gateway
// does not route `/internal/*`, so what this module exports is the vocabulary a
// console needs to READ the refusal: `PlanAllowance`, `PlanAllowances`,
// `PlanLimitReachedBody` and `isPlanLimitReached()`.
//
// Types come from `./generated/billing` (regenerate with
// `pnpm --filter @repletoryreach/sdk-api generate billing`); the request path
// stays on the house core so the envelope, cursor pagination, Idempotency-Key,
// token refresh and ApiError mapping behave exactly as before.
//
// ⚠ DRIFT (module-wide): `services/billing/Program.cs` serialises with
// `JsonIgnoreCondition.WhenWritingNull`, so every field the generated types
// declare `null | T` is **omitted** from the JSON rather than sent as `null`.
// Reading one yields `undefined`, not `null`. The `| null` declarations are
// kept exactly as generated because that is what the consoles compile against
// today: test them with a falsiness check, never `=== null`.
//
// Money is integer minor units throughout this module, C# `long` on the wire
// (the `int` money fields live on `subscriptions.Plan` and `admin.SkuRate`).
//
// The three gateway ITN webhooks: `/webhooks/payfast/itn`,
// `/webhooks/paystack/itn`, `/webhooks/stripe/itn`; are mapped with a bare
// `.Produces(200)` because each answers with one of two mutually exclusive
// bodies, so the OpenAPI document carries no 200 schema and the generator
// emits `200: unknown`. There is deliberately no SDK function for them: the
// payment gateways POST to them directly.

import { http } from './_core/request.js';
import { ApiError } from './_core/errors.js';
import type { Envelope, EnvelopeWithMeta, ListWindowMeta } from './_core/envelope.js';
import type { Page, PageOpts } from './_core/pagination.js';
import { buildPageQuery } from './_core/pagination.js';
import type * as gen from './generated/billing/index.js';

// ── Error codes ─────────────────────────────────────────────────────────────

/**
 * Machine-readable codes the billing service returns as `error.code`
 * (`ApiError.code`). `not_configured` is the W43 shape: HTTP 503 with body
 * `{ error: 'not_configured', provider }`: see {@link isNotConfigured}.
 *
 * Hand-written: the generated `ErrorBody.code` is an unconstrained `string`,
 * so there is nothing to derive this union from. Kept in step with the
 * literals in `services/billing/Endpoints/*.cs`.
 *
 * `INSUFFICIENT_CREDITS` is emitted by `services/billing` itself: a usage hold
 * the wallet cannot cover (HTTP 402) and a plan fee the bought credit cannot
 * pay (HTTP 402 with `required_minor`, `available_minor`, `included_minor` and
 * `shortfall_minor`). `services/channel-wa` also answers it at send time when
 * billing's credit check says no. While extra use is active (Growth and Scale,
 * 30 October 2026) the check counts the room left in the month, so it is
 * answered only once that room is used up too.
 */
export type BillingErrorCode =
  | 'INSUFFICIENT_CREDITS'
  | 'INVALID_AMOUNT'
  | 'UNKNOWN_PROVIDER'
  | 'PROVIDER_ERROR'
  | 'INVALID_SIGNATURE'
  | 'AMOUNT_MISMATCH'
  | 'INTENT_UNKNOWN'
  | 'INVOICE_NOT_FOUND'
  | 'INVOICE_INVALID'
  | 'ALREADY_SUBSCRIBED'
  | 'UNKNOWN_PLAN'
  | 'NO_SUBSCRIPTION'
  // ⚠ DRIFT; both are emitted by the admin invoice generator
  // (`AdminEndpoints.cs`) but were missing from this union before W36.
  | 'MISSING_ORG'
  | 'NO_LINES'
  // Plan allowances. ⚠ DRIFT: like `INSUFFICIENT_CREDITS`, this one is never
  // emitted by `services/billing`. Billing answers the allowance; the OWNING
  // service (channel-wa for numbers, identity for team members, flows-llm for
  // flows and knowledge documents) counts its rows and returns the 409. It
  // lives in this union because billing defines the code and both consoles
  // narrow on it: see {@link isPlanLimitReached}.
  | 'PLAN_LIMIT_REACHED'
  // 400 from `GET /internal/plan-allowance` for a limit outside the four.
  // Internal only; no console ever sees it.
  | 'UNKNOWN_LIMIT'
  // Extra use (30 October 2026): 400 `VALIDATION` names the field in
  // `error.field`; the five 409s say why it cannot be switched on.
  | 'VALIDATION'
  | ExtraUseErrorCode
  // 409 from moving up a plan (`subscriptions.update`) while a charge for
  // extra use is still being confirmed; try again in a few minutes. What extra
  // use owes is charged with the upgrade, so a declined card answers 402
  // `CARD_DECLINED` there too.
  | 'EXTRA_USE_PENDING'
  | 'not_configured';

/**
 * Body of the HTTP 503 returned when a payment/compliance provider has no
 * Settings → Integrations row. Generated as `NotConfiguredResponse`.
 */
export type NotConfiguredBody = Omit<gen.NotConfiguredResponse, 'error' | 'message'> & {
  /**
   * ⚠ DRIFT: generated as a bare `string` (the C# record types it
   * `string Error`). It is always the literal `not_configured`, and
   * {@link isNotConfigured} narrows on it, so the literal is pinned here.
   */
  error: 'not_configured';
  /**
   * ⚠ DRIFT: the service always sends `message` (non-nullable in the record),
   * but it stays optional here so existing narrowing keeps compiling.
   */
  message?: string;
};

/** True when `err` is the 503 `not_configured` response (body.error is the string code, not an object). */
export function isNotConfigured(err: unknown): err is ApiError & { body: NotConfiguredBody } {
  if (!(err instanceof ApiError) || err.status !== 503) return false;
  const body = err.body as { error?: unknown } | null;
  return !!body && typeof body === 'object' && body.error === 'not_configured';
}

// ── Plan allowances ─────────────────────────────────────────────────────────

/**
 * The allowances a plan carries. Mirrors `PlanProjection.LimitKeys` in
 * `services/billing/Services/PlanProjection.cs`, and the keys of `plans.limits`
 * on the price list.
 */
export type PlanLimitKey = 'bots' | 'numbers' | 'team_members' | 'flows' | 'knowledge_documents'
  /**
   * The business's own WhatsApp account (5 October 2026): `0` means not
   * included (Free, Lite, Plus), no ceiling means included (Growth, Scale).
   * A switch rather than a count; channel-wa asks it with a current of 0.
   */
  | 'own_whatsapp_account'
  /**
   * Bookings and Tickets (7 October 2026). `calendar_sync` is a switch like
   * `own_whatsapp_account` (`0` off, no ceiling on); the other four are counts.
   * Defaults per plan: `databases/billing/migrations/20261007_220_BookingsTicketsLimits.sql`.
   */
  | 'booking_staff'
  | 'booking_services'
  | 'calendar_sync'
  | 'ticket_types'
  | 'ticket_automations'
  /**
   * Extra use (30 October 2026): a switch like `own_whatsapp_account`. `0` means
   * the plan does not include it; no ceiling (null or absent) means it does.
   * Seeded on for Growth and Scale, `0` on Free, Lite and Plus.
   */
  | 'extra_use';

/** Every plan limit key, in the order the consoles list them. */
export const PLAN_LIMIT_KEYS: readonly PlanLimitKey[] = [
  'bots', 'numbers', 'team_members', 'flows', 'knowledge_documents', 'own_whatsapp_account',
  'booking_staff', 'booking_services', 'calendar_sync', 'ticket_types', 'ticket_automations', 'extra_use'
];

/** Limits that are a yes or no rather than a count: `0` is not included, no ceiling is included. */
export const PLAN_SWITCH_LIMIT_KEYS: readonly PlanLimitKey[] = ['own_whatsapp_account', 'calendar_sync', 'extra_use'];

/** True when a plan's `limits` include extra use: absent or no ceiling is on, `0` is off. */
export function includesExtraUse(limits: Record<string, number | null | undefined> | null | undefined): boolean {
  const v = limits?.extra_use;
  return !(typeof v === 'number' && v <= 0);
}

// ── Bookings and Tickets notices ───────────────────────────────────────────
//
// `POST /internal/notify/notice` on billing is how the Bookings and Tickets
// services tell a business something happened (7 October 2026). It is inside
// the service network, so there is no method here; what this module exports is
// the vocabulary: the kinds, the request body and the default channels, which
// the customer console's Settings → Notifications reads so it shows what the
// dispatcher (`services/billing/Services/Alerts/NotificationRouting.cs`,
// `NoticeDefaults`) will actually do.

/**
 * The notice kinds: Bookings and Tickets (7 October 2026), the inbox (9 October 2026), which goes by push alone
 * unless a person turns email on, and `channel_disconnected` (16 October 2026): Facebook stopped accepting a Page's
 * connection, so nobody can reply on Messenger or Instagram until it is connected again. `insights_ready` (29 October
 * 2026): a chat insights report is ready, could not finish, or the monthly one was skipped; to one person.
 * `flows_ready` (30 October 2026): the flows picked in Suggest flows with AI are built (switched off); to one person.
 * `flow_loose_ends` (2 November 2026): a step in a switched-on flow leads nowhere (it opens a deleted or switched-off
 * flow, a button has no link, a Web API step has no key), so customers who reach it get no reply; once per loose end.
 */
export type NoticeKind =
  | 'booking_request'
  | 'booking_new'
  | 'booking_changed'
  | 'ticket_new'
  | 'ticket_assigned'
  | 'ticket_breach'
  | 'task_due'
  | 'conversation_assigned'
  | 'conversation_waiting'
  | 'conversation_reply'
  | 'channel_disconnected'
  | 'insights_ready'
  | 'flows_ready'
  | 'flow_loose_ends';

export const NOTICE_KINDS: readonly NoticeKind[] = [
  'booking_request', 'booking_new', 'booking_changed',
  'ticket_new', 'ticket_assigned', 'ticket_breach', 'task_due',
  'conversation_assigned', 'conversation_waiting', 'conversation_reply',
  'channel_disconnected', 'insights_ready', 'flows_ready', 'flow_loose_ends'
];

/** The channels a notice can go out on. `push` reaches the person's own devices through Firebase (9 October 2026). */
export type NoticeChannel = 'email' | 'whatsapp' | 'push';

/** The channels each kind is on for somebody who has never chosen. */
export const NOTICE_KIND_DEFAULTS: Readonly<Record<NoticeKind, readonly NoticeChannel[]>> = {
  booking_request: ['email', 'whatsapp', 'push'],
  booking_new: ['email', 'push'],
  booking_changed: ['email', 'push'],
  ticket_new: ['email', 'push'],
  ticket_assigned: ['email', 'push'],
  ticket_breach: ['email', 'whatsapp', 'push'],
  task_due: ['email', 'push'],
  conversation_assigned: ['push'],
  conversation_waiting: ['push'],
  conversation_reply: ['push'],
  channel_disconnected: ['email', 'whatsapp', 'push'],
  insights_ready: ['email', 'push'],
  flows_ready: ['email', 'push'],
  flow_loose_ends: ['email', 'whatsapp', 'push']
};

/** Kinds written to one named person (`to_user_id`), by email and push, rather than to the organisation. */
export const PERSONAL_NOTICE_KINDS: readonly NoticeKind[] = [
  'ticket_assigned', 'task_due', 'conversation_assigned', 'conversation_reply', 'insights_ready', 'flows_ready'
];

export function isNoticeKind(kind: string): kind is NoticeKind {
  return (NOTICE_KINDS as readonly string[]).includes(kind);
}

/** One labelled line of a notice. `label_af` is the label for Afrikaans readers. */
export interface NoticeLine {
  label: string;
  value: string;
  label_af?: string;
}

/**
 * The body of `POST /internal/notify/notice` for a Bookings or Tickets kind.
 * `title` is one line (up to 200 characters); `lines` up to 10; `link_path` a
 * path in the customer console such as `/bookings/<id>` (default `/bookings`
 * or `/tickets`). `to_user_id` sends `ticket_assigned` and `task_due` to that
 * person alone when they are an active member.
 */
export interface NoticeRequest {
  organisation_id: string;
  kind: NoticeKind;
  title: string;
  title_af?: string;
  lines?: NoticeLine[];
  link_path?: string;
  to_user_id?: string;
}

/** What the notice endpoint answers. `sent` is the number of emails. */
export interface NoticeResult {
  sent: number;
  whatsapp: 'sent' | 'failed' | 'skipped';
  email_skipped?: string;
  whatsapp_detail?: string;
}

/** A stored notification preference: a whole-kind switch or a map of channel switches. */
type StoredPrefs = Record<string, boolean | Record<string, boolean> | undefined>;

/**
 * Whether one notification goes out on one channel, with the dispatcher's
 * defaults: a notice kind starts on the channels in
 * {@link NOTICE_KIND_DEFAULTS}, and a channel missing from a stored map takes
 * that default too. Any other kind is on everywhere until switched off, the
 * same answer as `identity.notificationAllowed`.
 */
export function noticeChannelAllowed(prefs: StoredPrefs | undefined, kind: string, channel: string): boolean {
  const fallback = isNoticeKind(kind) ? (NOTICE_KIND_DEFAULTS[kind] as readonly string[]).includes(channel) : true;
  const value = prefs?.[kind];
  if (value === undefined) return fallback;
  if (typeof value === 'boolean') return value;
  const onChannel = value[channel];
  return onChannel === undefined ? fallback : onChannel === true;
}

/**
 * What a plan allows for one limit.
 *
 * Billing resolves it; the service that owns the rows enforces it. There is
 * deliberately **no SDK method** for the allowance itself:
 * `GET /internal/plan-allowance` lives inside the service network and the
 * gateway does not route `/internal/*`, so no browser can reach it. The type is
 * exported because a console reading a 409 refusal, or a usage panel served
 * through a tenant-facing route, works in these terms.
 *
 * ⚠ DRIFT: `allowance` is **absent**, not null, when the plan puts no ceiling
 * on the limit (the service serialises with `WhenWritingNull`). Read
 * `unlimited`; never test `allowance === null`.
 */
export interface PlanAllowance {
  organisation_id: string;
  /** Absent only when the price book holds no active plan at all. */
  plan_code?: string;
  plan_name?: string;
  limit: PlanLimitKey;
  allowance?: number;
  unlimited: boolean;
}

/** All four allowances at once, for a usage panel. `unlimited` is the reliable half: see {@link PlanAllowance}. */
export interface PlanAllowances {
  organisation_id: string;
  plan_code?: string;
  plan_name?: string;
  allowances: Partial<Record<PlanLimitKey, number>>;
  unlimited: Record<PlanLimitKey, boolean>;
}

/**
 * The HTTP **409** body a service returns once an organisation has as many
 * numbers, team members, flows or knowledge documents as its plan allows.
 *
 * `message` is a whole sentence, already written for the customer ("You are on
 * Starter, which includes 1 number. Upgrade to add more."), so a console can
 * render it as it stands; the fields beside it are there for a console that
 * would rather compose its own copy from its own catalogue.
 */
export interface PlanLimitReachedBody {
  error: {
    code: 'PLAN_LIMIT_REACHED';
    message: string;
    limit: PlanLimitKey;
    /** What the plan allows. Absent when unlimited, which is a case that never refuses. */
    allowance?: number;
    /** How many the organisation already has. */
    current: number;
    plan_code?: string;
    plan_name?: string;
  };
}

/**
 * True when `err` is the 409 plan-allowance refusal. Narrows `err.body` so a
 * console can read `err.body.error.plan_name` and offer an upgrade instead of
 * showing "something went wrong".
 *
 * ```ts
 * try { await channelWa.addNumber(req); }
 * catch (e) {
 *   if (isPlanLimitReached(e)) showUpgrade(e.body.error);
 *   else throw e;
 * }
 * ```
 */
export function isPlanLimitReached(err: unknown): err is ApiError & { body: PlanLimitReachedBody } {
  if (!(err instanceof ApiError) || err.status !== 409) return false;
  const body = err.body as { error?: { code?: unknown } } | null;
  return !!body && typeof body === 'object' && !!body.error && body.error.code === 'PLAN_LIMIT_REACHED';
}

// ── Wallet + ledger ─────────────────────────────────────────────────────────

/**
 * The caller's wallet. `organisation_id`, `balance_minor` (minor units
 * cents / kobo) and `currency` come from the generated `WalletDto`; the two
 * fields below are this client's own.
 */
export type Wallet = gen.WalletDto & {
  /**
   * The part of `balance_minor` that came from a plan's included credits and is
   * not spent yet. Usage spends it first, and it is for messaging only: a plan
   * fee is never paid from it. By hand until the billing types are regenerated
   * (21 September 2026).
   */
  included_minor?: number;
  /** The rest of the balance: what a plan fee may be paid from. */
  purchased_minor?: number;
  /**
   * Pays by invoice (18 October 2026): the plan fee is taken from bought credit, and only RepletoryReach changes the
   * plan. The plan routes answer 409 `PLAN_BY_INVOICE` to the business itself.
   */
  pays_by_invoice?: boolean;
  /**
   * Held for something in progress (a phone call's next minutes, a fee awaiting approval): not spent,
   * and not spendable until it is given back (2 October 2026).
   */
  held_minor?: number;
  /** The balance less what is held. */
  available_minor?: number;
  /**
   * Extra use this month (30 October 2026), Growth and Scale. Absent (or null)
   * when extra use is not switched on. While it is active `balance_minor` can be
   * below zero: that is what is owed, and a console shows it as "Extra use this
   * month: R312,40 of R2 000", never as a negative balance.
   */
  extra_use?: WalletExtraUse | null;
  /**
   * ⚠ DRIFT, **not a wire field.** {@link getWallet} synthesises this alias
   * of `balance_minor` client-side. Kept because the consoles read it.
   * @deprecated alias of `balance_minor` (kept for existing pages).
   */
  credits_minor: number;
};

/** `ok`, `low` or `empty`: the wallet's state against the platform's low-balance line. */
export type WalletState = 'ok' | 'low' | 'empty';

/** The wallet's summary of extra use this month. See {@link ExtraUseView} for the whole of it. */
export interface WalletExtraUse {
  /** Switched on and not paused: spending goes on past zero, up to the limit. */
  active: boolean;
  mode: ExtraUseMode;
  /** The business's monthly limit. */
  limit_minor: number;
  /** Used this month: charged plus owed. */
  used_minor: number;
  /** What is left of the limit this month (0 when not active). */
  room_minor: number;
  /** Used beyond the credit and not charged yet. */
  owed_minor: number;
  /** Absent when not paused. */
  paused_reason?: ExtraUsePausedReason | null;
}

/** Mirrors `LedgerEntryTypeCodes` in `services/billing/Domain/Billing.cs`. */
export type LedgerEntryType = 'topup' | 'grant' | 'debit' | 'refund' | 'adjustment' | 'plan';

/**
 * One ledger row, from the generated `LedgerEntryDto`.
 *
 * `entry_type` is an unconstrained `string` on the wire; it is widened here
 * (`LedgerEntryType | string`) exactly as before so the known codes still
 * autocomplete without rejecting a new one.
 */
export type LedgerEntry = Omit<gen.LedgerEntryDto, 'entry_type'> & {
  entry_type: LedgerEntryType | string;
  /**
   * ⚠ DRIFT, **not a wire field.** {@link listLedger} copies `entry_type`
   * into it row by row. Kept because the tenant console's ledger table binds
   * a column to it.
   * @deprecated alias of `entry_type`.
   */
  transaction_type_code: string;
};

/** What the wire actually carries, before this module's two synthesised aliases. */
type RawWallet = gen.WalletDto;
/**
 * The `meta` a refund written for an undelivered WhatsApp message carries.
 *
 * Meta can accept a message over the API (HTTP 200, a `wamid` back) and refuse
 * it minutes later on the status webhook. The wallet has already been charged
 * by then, so channel-wa asks billing to reverse it and billing writes a
 * `refund` credit for exactly what was taken, keyed `refund:send:<id>` against
 * the debit's own `send:<id>`.
 *
 * Everything needed to answer "why is this credit here?" is on the row: which
 * message, to whom, and what Meta said, in Meta's numbers and in the
 * platform's own words.
 */
export interface FailedMessageRefundMeta {
  reason: 'meta_delivery_failed';
  /** The idempotency key of the debit this reverses, e.g. `send:0193…`. */
  reverses: string;
  reverses_entry_id: string;
  send_record_id: string;
  meta_message_id?: string | null;
  to_msisdn?: string | null;
  msg_type?: string | null;
  /** Meta's numeric code, e.g. "131042". */
  meta_error_code?: string | null;
  /** Meta's own title, e.g. "Business eligibility payment issue". */
  meta_error_title?: string | null;
  /** The platform's vocabulary for the same refusal: see `channelWa.DeliveryFailureCode`. */
  refusal_code?: string | null;
}

/**
 * True when a ledger row is the reversal of a message Meta accepted and then
 * did not deliver, narrowing `entry.meta` to {@link FailedMessageRefundMeta}.
 */
export function isFailedMessageRefund(
  entry: LedgerEntry
): entry is LedgerEntry & { meta: FailedMessageRefundMeta } {
  const m = (entry as { meta?: { reason?: unknown } | null }).meta;
  return entry.entry_type === 'refund' && !!m && m.reason === 'meta_delivery_failed';
}

type RawLedgerEntry = gen.LedgerEntryDto;

export async function getWallet(): Promise<Envelope<Wallet>> {
  const env = await http.get<Envelope<RawWallet>>('/v1/billing/wallet');
  return { ...env, data: { ...env.data, credits_minor: env.data.balance_minor } };
}

/**
 * What a ledger entry was for. Mirrors `LedgerSourceKinds` in
 * `services/billing/Domain/Billing.cs`.
 */
export type LedgerSourceKind =
  | 'broadcast' | 'flow' | 'agent' | 'inbound' | 'message' | 'kb' | 'notify' | 'media' | 'web'
  | 'topup' | 'auto_topup' | 'manual' | 'plan' | 'platform';

/** Query for {@link listLedger}. */
export interface LedgerListOpts extends PageOpts {
  /** Ledger entry type **code**: see {@link LedgerEntryType}. */
  entry_type?: LedgerEntryType | string;
  /** Row offset, for a numbered pager. Takes precedence over `cursor`. */
  offset?: number;
  /** ISO instant; only entries at or after it. */
  from?: string;
  /** ISO instant; only entries at or before it. */
  to?: string;
  /** Only entries of this source kind (broadcast, flow, agent, inbound …). */
  source_kind?: LedgerSourceKind | string;
  /** Only entries priced under this SKU. */
  sku?: string;
  /** Free text over the description, the contact number and the SKU. */
  q?: string;
}

/**
 * Ledger page metadata: the house page fields plus money totals for the WHOLE
 * filtered set. `debited_minor` is negative, as stored.
 *
 * The five named totals split the same money the way a wallet is read, each
 * row in exactly one, so with no filter
 * `added + refunded + paid_by_card - spent - plan_fees` is the balance. All
 * five are positive. `paid_by_card_minor` (11 October 2026) is plan fees paid
 * by card, written beside the fee so the credit balance does not move. `spent_minor` is usage only (messages, AI, storage,
 * notifications); a plan's fee, renewals and both sides of a plan change are
 * `plan_fees_minor`, and its included credits are in `added_minor`.
 */
export interface LedgerMeta {
  count: number;
  total: number;
  limit: number;
  credited_minor: number;
  debited_minor: number;
  added_minor: number;
  spent_minor: number;
  refunded_minor: number;
  plan_fees_minor: number;
  paid_by_card_minor?: number;
  next_cursor?: string | null;
}

export interface LedgerPage {
  data: LedgerEntry[];
  meta: LedgerMeta;
}

/**
 * The org's ledger, newest first. W46: `limit`, `cursor` and `entry_type` are
 * bound and honoured and `meta` carries `total` + `next_cursor`, so
 * `paginate()` walks the whole ledger. Before W46 only `limit` reached the
 * handler and `meta` was `{count}`, so paging stopped after one page.
 */
export async function listLedger(opts?: LedgerListOpts): Promise<LedgerPage> {
  const query: Record<string, string | number | undefined> = { ...buildPageQuery(opts) };
  if (opts?.entry_type !== undefined) query.entry_type = opts.entry_type;
  if (opts?.offset !== undefined) query.offset = opts.offset;
  if (opts?.from !== undefined) query.from = opts.from;
  if (opts?.to !== undefined) query.to = opts.to;
  if (opts?.source_kind !== undefined) query.source_kind = opts.source_kind;
  if (opts?.sku !== undefined) query.sku = opts.sku;
  if (opts?.q !== undefined) query.q = opts.q;
  const page = await http.get<LedgerPage & { data: RawLedgerEntry[] }>('/v1/billing/ledger', { query });
  return { ...page, data: page.data.map((e) => ({ ...e, transaction_type_code: e.entry_type })) };
}

// ── The cross-tenant journal (platform admin) ───────────────────────────────

/** One row of `GET /v1/admin/ledger`, from the generated `AdminLedgerEntryDto`. */
export type AdminLedgerEntry = gen.AdminLedgerEntryDto;

/**
 * Query for {@link listAdminLedger}. `since` / `until` bound the window in the
 * database, so a date range is the real period rather than a re-slice of
 * however many rows the cap returned.
 */
export interface AdminLedgerListOpts {
  org_id?: string;
  limit?: number;
  /** ISO instant; only entries at or after it. */
  since?: string;
  /** ISO instant; only entries at or before it. */
  until?: string;
  /** ISO-4217, e.g. `ZAR`. */
  currency?: string;
  /** Opaque keyset token from `meta.next_cursor`. Never parse it. */
  cursor?: string;
}

/**
 * Window metadata on the admin journal. `total` counts the whole window, not
 * the page; `capped` says the response stopped at the row cap.
 *
 * ⚠ DRIFT: `next_cursor` is not on the generated `AdminLedgerWindowMeta` yet.
 * The C# record grew the field in the same change; `src/generated/billing` is
 * committed output and only picks it up on the next
 * `pnpm --filter @repletoryreach/sdk-api generate billing`, so the field is
 * pinned by hand here. It is **absent** on the last page, not null.
 */
export interface AdminLedgerMeta extends ListWindowMeta {
  next_cursor?: string;
}

/**
 * The cross-tenant money journal, newest first, ordered
 * `occurred_at DESC, id DESC` and keyset-paged.
 *
 * ⚠ Overlaps `admin.listLedger`, which wraps the same route. That one predates
 * cursor paging and stops at the row cap; this is the cursor-capable form and
 * is what the admin console's Ledger page calls. The route belongs to the
 * billing service, which is why it is defined here.
 *
 * ```ts
 * for await (const row of paginate((cursor) => listAdminLedger({ cursor, limit: 500 }))) …
 * ```
 */
export function listAdminLedger(opts?: AdminLedgerListOpts): Promise<EnvelopeWithMeta<AdminLedgerEntry[], AdminLedgerMeta>> {
  const q: Record<string, string | number> = {};
  if (opts?.org_id) q.org_id = opts.org_id;
  if (opts?.limit !== undefined) q.limit = opts.limit;
  if (opts?.since) q.since = opts.since;
  if (opts?.until) q.until = opts.until;
  if (opts?.currency) q.currency = opts.currency;
  if (opts?.cursor) q.cursor = opts.cursor;
  return http.get<EnvelopeWithMeta<AdminLedgerEntry[], AdminLedgerMeta>>(
    '/v1/admin/ledger',
    Object.keys(q).length ? { query: q } : undefined
  );
}

/** `amount_minor` positive = grant, negative = adjustment/drain; `org_id` is platform-side only. */
export type GrantCreditsRequest = gen.GrantCreditsRequest;

export function grantCredits(req: GrantCreditsRequest): Promise<Envelope<gen.WalletBalanceDto>> {
  return http.post<Envelope<gen.WalletBalanceDto>>('/v1/billing/credits/grant', { body: req });
}

// ── Top-ups (payment intents) ───────────────────────────────────────────────

/**
 * ⚠ DRIFT: `TopupRequest.provider` and `TopupIntentDto.provider` are both
 * plain `string` in the generated types. The handler rejects anything outside
 * these three with 400 `UNKNOWN_PROVIDER`, so the union is pinned here (the
 * tenant console uses it as the value type of its provider `<Select>`).
 */
export type PaymentProvider = 'payfast' | 'paystack' | 'stripe';

/**
 * A way of paying the console may offer. `available` is false when an operator
 * has switched the provider off (`reason: 'disabled'`) or nobody has set it up
 * (`reason: 'not_configured'`). By hand until the billing types are regenerated
 * (21 September 2026).
 */
export interface PaymentProviderOffer {
  provider: PaymentProvider;
  name: string;
  /** ISO-4217 codes the provider takes. */
  currencies: string[];
  /** A card can be saved for automatic top-ups. */
  saves_cards: boolean;
  available: boolean;
  reason?: 'disabled' | 'not_configured' | null;
}

/**
 * The payment providers the console may offer now. Ask this instead of keeping
 * a list: a provider an operator switches off under Settings, Integrations stops
 * being `available` within seconds, and starting a payment through it answers
 * 409 `PROVIDER_DISABLED`.
 */
export function listPaymentProviders(): Promise<Envelope<PaymentProviderOffer[]>> {
  return http.get<Envelope<PaymentProviderOffer[]>>('/v1/payments/providers');
}

/**
 * SDK-side top-up request. Deliberately *not* `gen.TopupRequest`: that is the
 * wire body, on which every field is optional and `amount` / `amount_minor`
 * are both accepted (the original contract was `amount`). {@link topUp} sends
 * both from this one required field.
 */
export interface TopupRequest {
  /** Minor units. */
  amount_minor: number;
  /** ISO-4217; defaults to ZAR server-side. */
  currency?: string;
  /** Defaults to payfast. */
  provider?: PaymentProvider;
  /**
   * Where the gateway sends the payer afterwards (PayFast return_url / Paystack
   * callback_url). The platform adds `topup_id=<id>` to its query, so the page
   * can ask {@link getTopup} how the payment went.
   */
  return_url?: string;
}

/**
 * A created payment intent, from the generated `TopupIntentDto`.
 * `POST /v1/payments/topup` answers **201**, not 200.
 */
export type TopupIntent = Omit<
  gen.TopupIntentDto,
  'provider' | 'status' | 'authorization_url' | 'access_code' | 'reference'
> & {
  /** ⚠ DRIFT: `string` on the wire; see {@link PaymentProvider}. */
  provider: PaymentProvider;
  /** ⚠ DRIFT: `string` on the wire; a freshly created intent is always `pending`. */
  status: 'pending';
  // W41: present only when provider = paystack (from /transaction/initialize).
  // ⚠ DRIFT: generated as required `null | string`; `WhenWritingNull` omits
  // them for the other providers, so they are optional here.
  authorization_url?: string;
  access_code?: string;
  reference?: string;
};

/** @deprecated use {@link TopupIntent}. */
export type Payment = TopupIntent;

export function topUp(req: TopupRequest): Promise<Envelope<TopupIntent>> {
  // Both `amount` and `amount_minor` go out: the service reads whichever is
  // positive, and the original contract was `amount`.
  const body: gen.TopupRequest = {
    amount: req.amount_minor,
    amount_minor: req.amount_minor,
    currency: req.currency,
    provider: req.provider,
    return_url: req.return_url
  };
  return http.post<Envelope<TopupIntent>>('/v1/payments/topup', { body });
}

/** Where one payment stands (`GET /v1/payments/topup/{id}`). */
export interface TopupStatus {
  id: string;
  provider: PaymentProvider;
  provider_reference: string;
  amount_minor: number;
  currency: string;
  /** `pending` until the gateway confirms the payment, then `completed` or `failed`. */
  status: 'pending' | 'completed' | 'failed' | 'cancelled';
  /** `topup`, `save_method` or `auto_topup`. */
  purpose: string;
  created_at: string;
  // `WhenWritingNull` omits the two below until the payment is completed, so
  // they are optional here as well as nullable.
  completed_at?: string | null;
  /**
   * Credits that reached the wallet (minor units, 1 credit = 100), once the
   * payment is completed; absent before. Differs from `amount_minor` when the
   * payment was in another currency.
   */
  credited_minor?: number | null;
}

/**
 * Where one payment stands. The page a payer comes back to reads the
 * `topup_id` the platform added to its address and asks this until the
 * gateway has confirmed the payment. 404 `TOPUP_NOT_FOUND` outside the
 * caller's organisation.
 */
export function getTopup(id: string): Promise<Envelope<TopupStatus>> {
  return http.get<Envelope<TopupStatus>>(`/v1/payments/topup/${encodeURIComponent(id)}`);
}

// ── Invoices (W16 + W41 validation) ─────────────────────────────────────────
//
// ⚠ DRIFT: `status`, `validation_status`, `validation_validator` and
// `severity` are all unconstrained `string`s in the generated DTOs. The unions
// below mirror `InvoiceStatuses` / `InvoiceValidationStatuses` in
// `services/billing/Domain/Billing.cs` and the two validators' `Code`
// properties, and are pinned so the consoles keep their exhaustive switches.

export type InvoiceStatus = 'draft' | 'issued' | 'paid' | 'void' | 'past_due';
export type InvoiceValidator = 'sars' | 'firs';
export type InvoiceValidationStatus = 'unvalidated' | 'valid' | 'invalid' | 'skipped';

export type InvoiceValidationIssue = Omit<gen.InvoiceValidationIssueDto, 'severity'> & {
  /** `error` makes the invoice invalid; `warning` is informational. */
  severity: 'error' | 'warning';
};

/** Validate-only verdict: ZA → sars, NG → firs, other countries → validator null + valid true. */
export type InvoiceValidation = Omit<gen.InvoiceValidationDto, 'validator' | 'issues'> & {
  validator: InvoiceValidator | null;
  issues: InvoiceValidationIssue[];
};

/** List projection (`InvoiceShared.Summary`): no lines, no live verdict. */
export type Invoice = Omit<
  gen.InvoiceSummaryDto,
  'status' | 'validation_status' | 'validation_validator' | 'kind'
> & {
  kind: InvoiceKind;
  status: InvoiceStatus;
  /** Persisted verdict from the last generate / render / validate call. */
  validation_status: InvoiceValidationStatus;
  validation_validator: InvoiceValidator | null;
};

export type InvoiceLine = gen.InvoiceLineDto;

/** Detail projection: the summary fields plus `lines` and a freshly computed `validation`. */
export type InvoiceDetail = Omit<
  gen.InvoiceDetailDto,
  'status' | 'validation_status' | 'validation_validator' | 'validation' | 'kind'
> & {
  kind: InvoiceKind;
  status: InvoiceStatus;
  validation_status: InvoiceValidationStatus;
  validation_validator: InvoiceValidator | null;
  /** Fresh verdict computed for this response. */
  validation: InvoiceValidation;
};

export type InvoiceValidateResult = Omit<
  gen.InvoiceValidateResultDto,
  'validation' | 'validation_status'
> & {
  validation: InvoiceValidation;
  validation_status: InvoiceValidationStatus;
};

export function listInvoices(opts?: { status?: InvoiceStatus; limit?: number }): Promise<Envelope<Invoice[]>> {
  const q: Record<string, string | number> = {};
  if (opts?.status) q.status = opts.status;
  if (opts?.limit !== undefined) q.limit = opts.limit;
  return http.get<Envelope<Invoice[]>>('/v1/invoices', Object.keys(q).length ? { query: q } : undefined);
}

export function getInvoice(id: string): Promise<Envelope<InvoiceDetail>> {
  return http.get<Envelope<InvoiceDetail>>(`/v1/invoices/${encodeURIComponent(id)}`);
}

/** URL for `<a href>` / window.open; the PDF is validated before render and cached server-side. */
export function invoicePdfUrl(id: string): string {
  return `/v1/invoices/${encodeURIComponent(id)}/pdf`;
}

/**
 * Re-run the SARS/FIRS validator and persist the verdict. With `strict: true`
 * an invalid invoice rejects with HTTP 422 `INVOICE_INVALID` (the `ApiError.body.error.validation`
 * carries the issues); without it the verdict is always returned with 200.
 */
export function validateInvoice(id: string, opts?: { strict?: boolean }): Promise<Envelope<InvoiceValidateResult>> {
  return http.post<Envelope<InvoiceValidateResult>>(
    `/v1/invoices/${encodeURIComponent(id)}/validate`,
    opts?.strict ? { query: { strict: 'true' } } : undefined
  );
}

// ── Usage (2026-09-05 usage metering) ───────────────────────────────────────
//
// Where the money went. Every figure reads the attributed ledger: a debit
// names its SKU, its units and rate, and the broadcast, flow, conversation
// or contact it belongs to.

/** `GET /v1/billing/usage/summary`. */
export type UsageSummary = gen.UsageSummaryDto;
export type UsageCategory = gen.UsageCategoryDto;
export type UsageSku = gen.UsageSkuDto;
export type UsageDay = gen.UsageDayDto;

/**
 * Spend behind one broadcast, flow, conversation or contact. `amount_minor` is what was charged less what was
 * refunded (4 October 2026); `charged_minor` and `refunded_minor` are the two halves, by hand until the billing
 * types are regenerated.
 */
export type UsageSource = gen.UsageSourceDto & {
  charged_minor?: number;
  refunded_minor?: number;
};
/**
 * A unit price as this organisation pays it, in its wallet's currency.
 *
 * `rate_minor` is the price on the plan the organisation is on.
 * `list_rate_minor` is the pay-as-you-go price (equal to it with no paid plan),
 * and `plan_rates_minor` is the price on every plan on sale, keyed by plan code.
 * The last two are by hand until the billing types are regenerated
 * (21 September 2026).
 */
export type TenantRate = gen.TenantRateDto & {
  list_rate_minor?: number;
  plan_rates_minor?: Record<string, number>;
};

/** A plan on sale, as a column of a price comparison. */
export interface PlanRef {
  code: string;
  name: string;
}

/** What `listRates` adds under `meta`: whose prices these are, and the plans to compare them with. */
export interface TenantRatesPlanMeta {
  plan_code?: string | null;
  plan_name?: string | null;
  plans?: PlanRef[];
}

/**
 * The public price list: the active plans with their features and limits, the
 * live ZAR price book, the FX factors and the VAT switch. `GET /v1/public/pricing`
 * needs no token; the marketing site renders its pricing page from it.
 */
/**
 * A live rate. `amount_minor` is the pay-as-you-go price; `plan_amounts_minor`
 * is the price on every plan on sale, keyed by plan code (by hand until the
 * billing types are regenerated, 21 September 2026).
 */
export type PublicRate = gen.PublicRateDto & { plan_amounts_minor?: Record<string, number> };
export type PublicPricing = Omit<gen.PublicPricingDto, 'rates'> & { rates: PublicRate[] };
export type PublicPlan = gen.PlanDto;

export function getPublicPricing(): Promise<Envelope<PublicPricing>> {
  return http.get<Envelope<PublicPricing>>('/v1/public/pricing');
}

export type UsageSourceKind = 'broadcast' | 'flow' | 'flow_run' | 'conversation' | 'contact';

/**
 * Spend for a period. With no `from`/`to` the period is month-to-date and the
 * summary also carries `projected_month_end_minor`.
 */
export function getUsageSummary(opts?: { from?: string; to?: string }): Promise<Envelope<UsageSummary>> {
  const q: Record<string, string> = {};
  if (opts?.from) q.from = opts.from;
  if (opts?.to) q.to = opts.to;
  return http.get<Envelope<UsageSummary>>('/v1/billing/usage/summary', Object.keys(q).length ? { query: q } : undefined);
}

/**
 * Spend per id: what each broadcast cost, what each conversation has cost so
 * far. Up to 200 ids; ids with no spend are simply absent.
 */
export function getUsageSources(kind: UsageSourceKind, ids: string[]): Promise<Envelope<UsageSource[]>> {
  if (ids.length === 0) return Promise.resolve({ data: [] } as Envelope<UsageSource[]>);
  return http.get<Envelope<UsageSource[]>>('/v1/billing/usage/sources', { query: { kind, ids: ids.join(',') } });
}

export interface UsageEntriesMeta {
  count: number;
  total: number;
  limit: number;
  /** Spent by the whole set, not the page: charged less refunded. */
  spent_minor: number;
  /** Charged and refunded by the whole set (4 October 2026). */
  charged_minor?: number;
  refunded_minor?: number;
  next_cursor?: string | null;
}

export interface UsageEntriesPage {
  data: LedgerEntry[];
  meta: UsageEntriesMeta;
}

/** The ledger entries behind one broadcast, flow, conversation, contact or SKU. */
export async function listUsageEntries(kind: UsageSourceKind | 'sku', id: string, opts?: PageOpts): Promise<UsageEntriesPage> {
  const query: Record<string, string | number | undefined> = { ...buildPageQuery(opts), kind, id };
  const page = await http.get<UsageEntriesPage & { data: RawLedgerEntry[] }>('/v1/billing/usage/entries', { query });
  return { ...page, data: page.data.map((e) => ({ ...e, transaction_type_code: e.entry_type })) };
}

/** `POST /v1/billing/usage/estimate`: what a WhatsApp send will cost before it goes out. */
export type UsageEstimate = gen.UsageEstimateDto;

export interface UsageEstimateRequest {
  /** WhatsApp template category: marketing, utility, authentication or service. Default utility. */
  category?: string;
  /** Recipients, priced each by dialling code. Up to 50 000. */
  recipients?: string[];
  /** A bare count, priced at `country` (za, ng, ke, else rest of world). */
  recipient_count?: number;
  country?: string;
}

/**
 * The same price book and country rule the meter applies afterwards, so the
 * figure quoted before a send is the figure that lands on the ledger. Carries
 * the balance, the shortfall and `ok`.
 */
export function estimateUsage(req: UsageEstimateRequest): Promise<Envelope<UsageEstimate>> {
  return http.post<Envelope<UsageEstimate>>('/v1/billing/usage/estimate', { body: req });
}

/**
 * The month's usage statement (`YYYY-MM`, a closed month): a paid tax invoice
 * with one line per SKU, built now if the monthly pass has not yet. Idempotent.
 * 400 `PERIOD_OPEN` for the current month, 404 `NO_USAGE` for a month with
 * nothing charged.
 */
export function generateStatement(period: string): Promise<Envelope<Invoice> & { meta: { created: boolean } }> {
  return http.post<Envelope<Invoice> & { meta: { created: boolean } }>(`/v1/invoices/statements/${encodeURIComponent(period)}`);
}

/** `invoice`, or `usage_statement` for a month's consumption built from the ledger. */
export type InvoiceKind = 'invoice' | 'usage_statement';

/**
 * The price list in the wallet's currency: what a message, a thousand tokens, a
 * file, a GB-day costs this organisation on the plan it is on. `meta` also says
 * whose prices these are (`plan_code`, `plan_name`) and lists the plans on sale,
 * so a page can compare them with `plan_rates_minor` on each row.
 */
export function listRates(): Promise<TenantRatesEnvelope> {
  return http.get<TenantRatesEnvelope>('/v1/billing/usage/rates');
}

/** `listRates`: the currency and VAT fields every price list carries, and the plan the prices are for. */
export type TenantRatesEnvelope = Envelope<TenantRate[]> & {
  meta: EnvelopeWithCurrency<TenantRate[]>['meta'] & TenantRatesPlanMeta;
};

export interface EnvelopeWithCurrency<T> extends Envelope<T> {
  meta: {
    currency: string;
    /** Whether VAT is added on top of the listed rates for this organisation. */
    vat_applies: boolean;
    vat_percent: number;
    vat_label: string;
    vat_registration_number?: string | null;
  };
}

// ── Wallet alerts and auto top-up ───────────────────────────────────────────

/** `GET` and `PUT /v1/billing/alerts`. */
export type WalletAlerts = gen.WalletAlertsDto;
export type WalletAlertSettings = gen.WalletAlertSettingsDto;
export type UpdateWalletAlertsRequest = gen.UpdateWalletAlertsRequest;
/** One alert the platform sent, with its outcome per channel. */
export type WalletNotification = gen.WalletNotificationDto;
/**
 * The wallet alerts. The four `extra_use_*` kinds (30 October 2026) go by email, WhatsApp and push:
 * `extra_use_started` the credit ran out and extra use is carrying on; `extra_use_charged` the card was charged for
 * extra use; `extra_use_declined` a charge was declined, so extra use is paused; `extra_use_limit` the monthly limit
 * is reached and sending stops as it does at zero. Upfront top-ups reuse `auto_topup_succeeded` and
 * `auto_topup_failed`.
 */
export type WalletNotificationKind =
  | 'low_balance' | 'depleted' | 'recovered' | 'auto_topup_succeeded' | 'auto_topup_failed' | 'daily_spend'
  | 'extra_use_started' | 'extra_use_charged' | 'extra_use_declined' | 'extra_use_limit';

export const WALLET_NOTIFICATION_KINDS: readonly WalletNotificationKind[] = [
  'low_balance', 'depleted', 'recovered', 'auto_topup_succeeded', 'auto_topup_failed', 'daily_spend',
  'extra_use_started', 'extra_use_charged', 'extra_use_declined', 'extra_use_limit'
];
export type WalletNotificationChannel = 'email' | 'whatsapp' | 'webhook' | 'in_app';

export function getAlerts(): Promise<Envelope<WalletAlerts>> {
  return http.get<Envelope<WalletAlerts>>('/v1/billing/alerts');
}

/**
 * Partial update: only the fields present change. Turning auto top-up on
 * needs an amount and a saved payment method, else 400 `AUTO_TOPUP_AMOUNT`
 * or `AUTO_TOPUP_NO_METHOD`.
 */
export function updateAlerts(req: UpdateWalletAlertsRequest): Promise<Envelope<WalletAlerts>> {
  return http.put<Envelope<WalletAlerts>>('/v1/billing/alerts', { body: req });
}

export function listAlertHistory(opts?: { limit?: number }): Promise<Envelope<WalletNotification[]>> {
  return http.get<Envelope<WalletNotification[]>>('/v1/billing/alerts/history', opts?.limit ? { query: { limit: opts.limit } } : undefined);
}

/** Send the low-balance alert now, to see it arrive. */
export function sendTestAlert(): Promise<Envelope<gen.WalletAlertTestDto>> {
  return http.post<Envelope<gen.WalletAlertTestDto>>('/v1/billing/alerts/test');
}

// ── Extra use (30 October 2026) ─────────────────────────────────────────────
//
// On Growth and Scale, with a saved card, a business can switch on extra use:
// after its credit runs out everything keeps working at its plan's prices, up to
// a monthly limit it sets. It chooses how that is paid. The plans read
// "Growth+" and "Scale+" while it is on. A plan includes it unless
// `plans.limits.extra_use` is 0 (see {@link includesExtraUse}).

/**
 * How extra use is paid. `steps`: the card is charged each time what is owed
 * reaches the step amount. `upfront`: the card buys credit (a top-up of
 * `topup_minor`) when the balance drops below `topup_below_minor`, so the
 * balance never goes below zero. `month_end`: the card is charged on the 1st,
 * just before the plan fee.
 */
export type ExtraUseMode = 'steps' | 'upfront' | 'month_end';

export const EXTRA_USE_MODES: readonly ExtraUseMode[] = ['steps', 'upfront', 'month_end'];

/**
 * Why extra use is not working although it is switched on. `declined` comes
 * with `decline_reason` (the card provider's words, may be absent);
 * `past_due` the plan is waiting for payment; `blocked` the platform switched it
 * off for this business; `no_card` no card is saved; `not_on_plan` and
 * `not_offered` the plan or the platform does not offer it now.
 */
export type ExtraUsePausedReason = 'no_card' | 'declined' | 'past_due' | 'blocked' | 'not_on_plan' | 'not_offered';

/** The 409 codes `updateExtraUse` answers with when extra use cannot be switched on. */
export type ExtraUseErrorCode =
  | 'EXTRA_USE_NOT_ON_PLAN'
  | 'EXTRA_USE_NO_CARD'
  | 'EXTRA_USE_NOT_OFFERED'
  | 'EXTRA_USE_BLOCKED'
  | 'EXTRA_USE_PAST_DUE';

/**
 * What happens next. `step`: the card is charged when what is owed reaches
 * `at_minor`. `topup`: a top-up when the balance drops below `at_minor`.
 * `month_end`: charged on `on` (`YYYY-MM-DD`) with the plan fee.
 */
export interface ExtraUseNextCharge {
  kind: 'step' | 'topup' | 'month_end';
  at_minor?: number | null;
  on?: string | null;
}

/** The platform's limits on what a business may choose. */
export interface ExtraUseBounds {
  min_limit_minor: number;
  max_limit_minor: number;
  min_step_minor: number;
}

/**
 * `GET` and `PUT /v1/billing/extra-use`. Amounts are minor units (cents) of
 * `currency`. The month is the South African calendar month.
 *
 * ⚠ The service writes with `WhenWritingNull`, so the nullable fields are
 * absent rather than null: test them with a falsiness check.
 */
export interface ExtraUseView {
  /** The platform offers extra use at all. */
  offered: boolean;
  /** The business's plan includes it (Growth and Scale). */
  on_plan: boolean;
  plan_code?: string | null;
  plan_name?: string | null;
  /** A card is saved to charge. */
  has_card: boolean;
  card_label?: string | null;
  enabled: boolean;
  mode?: ExtraUseMode | null;
  limit_minor?: number | null;
  step_minor?: number | null;
  topup_minor?: number | null;
  topup_below_minor?: number | null;
  /** Enabled and not paused: extra use is working now. */
  active: boolean;
  /** Only meaningful when `enabled`. */
  paused_reason?: ExtraUsePausedReason | null;
  paused_at?: string | null;
  decline_reason?: string | null;
  /** `YYYY-MM`. */
  month: string;
  /** Already charged to the card for extra use this month (steps or top-ups). */
  charged_minor: number;
  /** Used beyond the credit and not charged yet. */
  owed_minor: number;
  /** Charged plus owed. */
  used_minor: number;
  /** The limit less what is used (0 when not active). */
  room_minor: number;
  /** Absent when nothing is coming. */
  next_charge?: ExtraUseNextCharge | null;
  bounds: ExtraUseBounds;
  currency: string;
}

/**
 * The body of `PUT /v1/billing/extra-use`. `{ enabled: false }` alone switches
 * it off: the settings are kept, and anything still owed is charged at month
 * end. Switching on needs `mode` and `limit_minor` (between the bounds),
 * `step_minor` for `steps` and `topup_minor` for `upfront` (each at least
 * `bounds.min_step_minor` and at most the limit); `topup_below_minor` is
 * optional for `upfront` (default 5000, R50).
 */
export interface ExtraUseUpdate {
  enabled: boolean;
  mode?: ExtraUseMode | null;
  limit_minor?: number | null;
  step_minor?: number | null;
  topup_minor?: number | null;
  topup_below_minor?: number | null;
}

/** Extra use for the caller's organisation. Any signed-in member may read it. */
export function getExtraUse(): Promise<Envelope<ExtraUseView>> {
  return http.get<Envelope<ExtraUseView>>('/v1/billing/extra-use');
}

/**
 * Switches extra use on or off, or changes its settings. Owners and admins
 * only (403 otherwise). 400 `VALIDATION` with `error.field`; 409
 * {@link ExtraUseErrorCode} when it cannot be switched on.
 */
export function updateExtraUse(req: ExtraUseUpdate): Promise<Envelope<ExtraUseView>> {
  return http.put<Envelope<ExtraUseView>>('/v1/billing/extra-use', { body: req });
}

// ── Saved payment methods ───────────────────────────────────────────────────

/** A saved gateway authorisation. Never a card number. */
export type PaymentMethod = gen.PaymentMethodDto;

export function listPaymentMethods(): Promise<Envelope<PaymentMethod[]>> {
  return http.get<Envelope<PaymentMethod[]>>('/v1/payments/methods');
}

/**
 * Start saving a card: a small card-only Paystack charge whose authorisation
 * is reusable. The amount is credited to the wallet. Answers 201 with the
 * checkout URL; the card appears in {@link listPaymentMethods} once the
 * gateway confirms. Other providers answer 501 `PROVIDER_UNSUPPORTED`.
 */
export function savePaymentMethod(req?: { provider?: PaymentProvider; return_url?: string }): Promise<Envelope<TopupIntent & { purpose: 'save_method' }>> {
  return http.post<Envelope<TopupIntent & { purpose: 'save_method' }>>('/v1/payments/methods', { body: req ?? {} });
}

export function setPrimaryPaymentMethod(id: string): Promise<Envelope<PaymentMethod[]>> {
  return http.post<Envelope<PaymentMethod[]>>(`/v1/payments/methods/${encodeURIComponent(id)}/primary`);
}

export function removePaymentMethod(id: string): Promise<Envelope<PaymentMethod[]> & { meta: { auto_topup_enabled: boolean } }> {
  return http.delete<Envelope<PaymentMethod[]> & { meta: { auto_topup_enabled: boolean } }>(`/v1/payments/methods/${encodeURIComponent(id)}`);
}

/** Charge the saved method for the configured auto top-up amount now. */
export function runAutoTopup(): Promise<Envelope<gen.AutoTopupRunDto>> {
  return http.post<Envelope<gen.AutoTopupRunDto>>('/v1/payments/auto-topup/run');
}

// ── the partner who referred this company (W47) ─────────────────────────────

/** Who introduced this account, if anyone, and whether a code could still be entered. */
export function getReferral(): Promise<Envelope<gen.ReferredByDto>> {
  return http.get<Envelope<gen.ReferredByDto>>('/v1/billing/referral-code');
}

/**
 * Enters a partner's code after signing up. Allowed for a short while and
 * only before the first payment, because commission on usage a partner had no
 * hand in is not theirs.
 *
 * 404 `CODE_UNKNOWN`, 409 `ALREADY_REFERRED`, 400 `SELF_REFERRAL`, 409
 * `TOO_LATE`, 403 `PROGRAMME_DISABLED`.
 */
export function applyReferralCode(code: string): Promise<Envelope<gen.ReferredByDto>> {
  return http.post<Envelope<gen.ReferredByDto>>('/v1/billing/referral-code', { body: { code } });
}
